import { useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import { isSameYear } from 'date-fns'
import { useErrorHandler } from 'react-error-boundary'

import { Button } from '@wartek-id/button-v1'
import { Badge } from '@wartek-id/badge'
import { Icon } from '@wartek-id/icon-v2'
import { Separator } from '@wartek-id/separator'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import { Header } from 'app/KinerjaShared/components/Header'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { assetUrl } from 'utils/url'
import { formatDate } from 'utils/datetime'
import { useAPIGetPenetapanSummary } from 'api/kinerja/pengawas-sekolah/penetapan'
import { PANDUAN_HASIL_PENETAPAN_PREDIKAT_URL } from 'app/KinerjaPSPegawaiPage/constants/panduan'
import {
  PENETAPAN_PEGAWAI_PREDIKAT_RATING_OPTIONS,
  PENETAPAN_PEGAWAI_RATING_BADGE_COLORS,
} from 'app/KinerjaTimPenilaiPSPage/constants/penetapan'
import { PERFORMANCE_PRACTICE_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/performancePractice'
import { WORK_BEHAVIOR_RESULT_TEXT } from 'app/KinerjaPage/pages/PelaksanaanV2/constants/workBehavior'
import { NotFoundError } from 'utils/customError'
import { usePenetapanTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePenetapanTrackEvent'

const PenetapanResults = () => {
  const router = useRouter()
  const { period } = usePeriodContext()
  const handleError = useErrorHandler()
  const { trackEvent } = usePenetapanTrackEvent()

  // Get Penetapan Pegawai Summary
  const {
    data: responseData,
    isFetching,
    isSuccess,
  } = useAPIGetPenetapanSummary({
    options: {
      staleTime: 60 * 60 * 1000, // 1 hour
      useErrorBoundary: true,
    },
  })

  const isPenetapanFinished = useMemo(
    () =>
      ['SENDING_TO_BKN', 'SENT_TO_BKN', 'ERROR_SEND'].includes(
        responseData?.status
      ),
    [responseData?.status]
  )

  const actorLabels = {
    nama: 'Nama',
    nip: 'NIP',
    jabatan: 'Jabatan',
    unitKerja: 'Unit Kerja',
  }

  const evaluationPeriod = useMemo(() => {
    const startDate = period?.startDate
    const endDate = period?.endDate

    if (!startDate || !endDate) {
      return '-'
    }

    const start = new Date(startDate)
    const end = new Date(endDate)

    const startFormat = formatDate(
      start,
      isSameYear(start, end) ? 'd MMMM' : 'd MMMM yyyy'
    )
    const endFormat = formatDate(end, 'd MMMM yyyy')

    return `${startFormat} sampai dengan ${endFormat}`
  }, [period])

  useEffect(() => {
    if (!isSuccess) {
      return
    }

    if (isPenetapanFinished) {
      trackEvent('hasil penilaian kinerja', 'view')
    } else {
      return handleError(
        new NotFoundError(null, new Error('Page is not available'))
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPenetapanFinished, isSuccess])

  return (
    <>
      <div className="bg-white py-9">
        <div className="mx-auto max-w-7xl">
          <Header
            title="Hasil Penilaian Kinerja"
            className="print:hidden"
            onBack={router.back}
            rightNode={
              <Button
                variant="ghost"
                as="a"
                href={PANDUAN_HASIL_PENETAPAN_PREDIKAT_URL}
                target="_blank"
                className="space-x-1"
              >
                <Icon color="informational">help</Icon>
                <Text variant="action" color="primary">
                  Ke Pusat Informasi
                </Text>
              </Button>
            }
          />
        </div>
      </div>

      {isFetching && (
        <div className="flex justify-center py-12">
          <div className="flex flex-row p-2 mx-auto">
            <Spinner />
            <Text as="span" className="pl-4" variant="body">
              Harap tunggu, Dokumen sedang dimuat
            </Text>
          </div>
        </div>
      )}

      {isSuccess && isPenetapanFinished && (
        <div className="px-8 pt-10 pb-32 mx-auto max-w-7xl xl:px-0">
          <div className="p-8 !bg-gradient-to-r from-blue-90 to-blue-80 overflow-hidden relative rounded-t-lg">
            <img
              alt=""
              role="presentation"
              src={assetUrl('/images/pengelolaan-kinerja/confetti.png')}
              className="absolute top-0 right-0 z-0"
              height={60}
              width={144}
            />
            <div className="flex items-center gap-6">
              <div className="flex flex-col items-start justify-center flex-1 gap-1">
                <div className="flex flex-col items-start gap-2">
                  <Text variant="heading-lg" color="inverse">
                    Hasil Penilaian Kinerja Pengawas Sekolah
                  </Text>
                </div>
              </div>
            </div>
          </div>
          <div className="p-8 bg-white rounded-b-lg">
            <div className="w-full space-y-8 xl:w-8/12">
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Text variant="heading-sm">Nama Instansi</Text>
                  <Text>{responseData?.dinasPendidikan}</Text>
                </div>
                <div className="space-y-1">
                  <Text variant="heading-sm">Periode Penilaian</Text>
                  <Text>{evaluationPeriod}</Text>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <Text variant="heading-sm">Pegawai Yang Dinilai</Text>
                </div>
                {Object.keys(actorLabels).map((key) => {
                  const label = actorLabels[key]
                  const item = responseData?.pengawasSekolah?.[key]
                  return (
                    <>
                      <div className="flex justify-between">
                        <Text>{label}</Text>
                        <span>:</span>
                      </div>
                      <div>
                        <Text>{item}</Text>
                      </div>
                    </>
                  )
                })}
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <Text variant="heading-sm">Pejabat Penilai Kinerja</Text>
                </div>
                {Object.keys(actorLabels).map((key) => {
                  const label = actorLabels[key]
                  const value = responseData?.atasan?.[key]
                  return (
                    <>
                      <div className="flex justify-between">
                        <Text>{label}</Text>
                        <span>:</span>
                      </div>
                      <div>
                        <Text>{value}</Text>
                      </div>
                    </>
                  )
                })}
              </div>

              <Separator />

              <div className="mb-8 space-y-4">
                <div className="flex space-x-2">
                  <Text variant="heading-sm">Predikat Kinerja</Text>
                  <Badge className="!bg-expressive-dark-blue !text-white">
                    Tahunan
                  </Badge>
                </div>

                <div className="border rounded-md border-subdued">
                  <div className="grid items-center grid-cols-12 gap-6 border-b border-subdued">
                    <div className="col-span-8 p-6">
                      <Text variant="heading-sm">Praktik Kinerja</Text>
                      <Text variant="body-sm" color="subdued">
                        Berdasarkan dokumen Tindak Lanjut dan Refleksi Tindak
                        Lanjut
                      </Text>
                    </div>
                    <div className="col-span-4 p-6">
                      <Badge
                        variant="success"
                        className={
                          PENETAPAN_PEGAWAI_RATING_BADGE_COLORS?.[
                            responseData?.praktikKinerjaRating
                          ]
                        }
                      >
                        {
                          PERFORMANCE_PRACTICE_RESULT_TEXT?.[
                            responseData?.praktikKinerjaRating
                          ]
                        }
                      </Badge>
                    </div>
                  </div>
                  <div className="grid items-center grid-cols-12 gap-6 border-b border-subdued">
                    <div className="col-span-8 p-6">
                      <Text variant="heading-sm">Perilaku Kerja</Text>
                      <Text variant="body-sm" color="subdued">
                        Berdasarkan penilaian terhadap 7 aspek Perilaku Kerja
                      </Text>
                    </div>
                    <div className="col-span-4 p-6">
                      <Badge
                        variant="success"
                        className={
                          PENETAPAN_PEGAWAI_RATING_BADGE_COLORS?.[
                            responseData?.perilakuKerjaRating
                          ]
                        }
                      >
                        {
                          WORK_BEHAVIOR_RESULT_TEXT?.[
                            responseData?.perilakuKerjaRating
                          ]
                        }
                      </Badge>
                    </div>
                  </div>
                  <div className="grid items-center grid-cols-12 gap-6 bg-surface-selected-default rounded-b-md">
                    <div className="col-span-8 p-6">
                      <Text variant="heading-md">Predikat Kinerja</Text>
                    </div>
                    <div className="col-span-4 p-6">
                      <Text variant="heading-md">
                        {
                          PENETAPAN_PEGAWAI_PREDIKAT_RATING_OPTIONS?.[
                            responseData?.predikatKinerja
                          ]
                        }
                      </Text>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Text variant="heading-sm">Catatan:</Text>

                <ol className="list list-decimal !pl-8 space-y-1">
                  <li>Dokumen ini tidak perlu ditandatangani.</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PenetapanResults
